/**
 * Lead Lifecycle Dashboard — four reports the spec defines as the
 * "dashboard implications" of the unified pipeline:
 *
 *   1. Pipeline funnel (leads per stage + conversion %)
 *   2. Stage aging report (leads sitting too long)
 *   3. Status mix per agent (Hot / Warm / Cold counts)
 *   4. Drop reasons (most common Lost reasons for coaching)
 *
 * Manager-facing — non-managers see a 403 card. We don't gate at the
 * router level so a shared link still produces a useful message.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Activity, AlertTriangle, BarChart3, RefreshCw, Clock, Users,
  Flame, Settings as SettingsIcon, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LeadStageBadge, LeadTemperaturePill, CloseProbabilityBadge,
  useLifecycleEnums, LifecycleEnumsProvider,
} from '@/components/LeadLifecycle';
import HelpInfo from '@/components/HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isManagerRole = (role) => {
  const r = (role || '').toLowerCase();
  return ['admin', 'owner', 'ceo', 'hod', 'manager', 'super_admin', 'indirect_sales_lead']
    .some((h) => r.includes(h));
};

export default function LeadLifecycleDashboard() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('funnel');
  const isManager = isManagerRole(user?.role);

  if (!isManager) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={20} /> Manager access required
            </CardTitle>
            <CardDescription>
              The lifecycle reports are scoped to managers and admins.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <LifecycleEnumsProvider token={token}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto p-6 space-y-5">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
              <Activity size={26} className="text-violet-500" />
              Lead Lifecycle
              <HelpInfo
                align="inline"
                title="How the Lead Lifecycle dashboard works"
                steps={[
                  <>Manager-facing report set that gives you visibility into the <strong>health of the pipeline</strong> — where leads are, how long they've been sitting, which agents handle them, and why they fall out. Useful for spotting bottlenecks before they cost revenue.</>,
                  <><strong>Funnel</strong> — leads grouped by lifecycle stage (Enquiry → Briefing → Qualified → Site Visit → Negotiation → Booking → Closed) with stage-to-stage conversion %. Big drop between two stages = a coaching opportunity.</>,
                  <><strong>Aging</strong> — leads sitting longer than the configured threshold for their current stage (e.g. >7 days in Qualified). Stale leads are the most common revenue leak; this tab shows exactly who needs a nudge. <em>Empty state means everyone's within their thresholds — that's a good sign.</em></>,
                  <><strong>Agent mix</strong> — per-agent breakdown of Hot / Warm / Cold lead counts. Highlights agents sitting on a lot of cold inventory (re-allocate) or hot leads with no recent activity (escalate).</>,
                  <><strong>Drop reasons</strong> — most common <em>Lost</em> outcomes across the team (price, timing, unresponsive, competitor, no budget…). Reveals coaching topics + product / pricing feedback for marketing.</>,
                  <><strong>Settings</strong> — admin-only. Configure the aging thresholds per stage. The dashboard reflects new thresholds within seconds.</>,
                  <><strong>Refresh</strong> pulls live data without changing your tab. The report uses your current org's lead pool — sales agents see their own leads, HODs/CEOs see the whole team.</>,
                ]}
                tip="If Aging or Drop reasons show 'Could not load', the backend route hasn't finished booting or there's an index issue — try Refresh in 30 seconds before assuming there's no data."
              />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Pipeline funnel, stage aging, agent temperature mix, and drop-reasons.
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="funnel"><BarChart3 size={14} className="mr-1.5" /> Funnel</TabsTrigger>
              <TabsTrigger value="aging"><Clock size={14} className="mr-1.5" /> Aging</TabsTrigger>
              <TabsTrigger value="mix"><Users size={14} className="mr-1.5" /> Agent mix</TabsTrigger>
              <TabsTrigger value="drops"><TrendingDown size={14} className="mr-1.5" /> Drop reasons</TabsTrigger>
              <TabsTrigger value="settings"><SettingsIcon size={14} className="mr-1.5" /> Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="funnel"><FunnelTab token={token} /></TabsContent>
            <TabsContent value="aging"><AgingTab token={token} /></TabsContent>
            <TabsContent value="mix"><AgentMixTab token={token} /></TabsContent>
            <TabsContent value="drops"><DropReasonsTab token={token} /></TabsContent>
            <TabsContent value="settings"><SettingsTab token={token} /></TabsContent>
          </Tabs>
        </div>
      </div>
    </LifecycleEnumsProvider>
  );
}

// ---- Funnel

const FunnelTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lead-lifecycle/funnel?token=${token}`);
      setData(res.data?.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load funnel');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Pipeline funnel</CardTitle>
          <CardDescription>Lead counts per stage + conversion between adjacent stages.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <Skeleton className="h-40 w-full" />
        ) : !data ? (
          <div className="text-slate-500">No data.</div>
        ) : (
          <div className="space-y-2">
            {data.funnel.map((row) => {
              // Width relative to the FIRST stage (biggest by definition).
              const max = data.funnel[0]?.count || 1;
              const pct = max ? (100 * row.count) / max : 0;
              return (
                <div key={row.stage} className="flex items-center gap-3">
                  <div className="w-32 text-sm">{row.label}</div>
                  <div className="flex-1 h-7 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden relative">
                    <div
                      className="h-full bg-violet-500/80"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 px-2 flex items-center text-xs font-medium">
                      {row.count}
                    </div>
                  </div>
                  <div className="w-24 text-xs text-right text-slate-500">
                    {row.conversion_from_previous_pct !== null
                      ? `${row.conversion_from_previous_pct}% from prev`
                      : ''}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-slate-400 mt-2">
              Total leads in window: {data.total}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Aging

const AgingTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lead-lifecycle/aging?token=${token}`);
      setRows(res.data?.data?.leads || []);
    } catch (e) {
      toast.error('Could not load aging report');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Stage aging</CardTitle>
          <CardDescription>Leads sitting longer than the configured threshold in their stage.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading && !rows.length ? (
          <Skeleton className="h-40 w-full" />
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <Activity size={32} className="mx-auto text-emerald-400 mb-2" />
            All leads within their stage thresholds.
          </div>
        ) : (
          <div className="space-y-1">
            {rows.map((r) => (
              <div key={r.id + r.collection} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.client_name || r.name || '—'}</span>
                    <LeadStageBadge stage={r.stage} sub_status={r.stage_sub_status} compact />
                    <LeadTemperaturePill status={r.temperature_status} readOnly compact />
                    <CloseProbabilityBadge leadId={r.id} token={token} collection={r.collection} compact />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Owner: {r.assigned_to_name || '—'}
                    {r.broker_name && <span> · Broker: {r.broker_name}</span>}
                    {' · '}collection: {r.collection}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-red-600">{r.age_days}d</div>
                  <div className="text-[10px] text-slate-400">threshold {r.threshold_days}d</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Agent mix

const AgentMixTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const enums = useLifecycleEnums();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lead-lifecycle/status-mix?token=${token}`);
      setRows(res.data?.data?.agents || []);
    } catch (e) {
      toast.error('Could not load agent mix');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Agent temperature mix</CardTitle>
          <CardDescription>How many Hot / Warm / Cold leads each agent owns.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading && !rows.length ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 px-3">Agent</th>
                  {enums.temperatures.map((t) => (
                    <th key={t.key} className="text-center py-2 px-2">
                      <span title={t.label}>{t.emoji}</span>
                    </th>
                  ))}
                  <th className="text-right py-2 px-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.agent_id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 px-3 font-medium">{r.agent_name}</td>
                    {enums.temperatures.map((t) => (
                      <td key={t.key} className="text-center py-2 px-2">
                        {r.counts[t.key] || 0}
                      </td>
                    ))}
                    <td className="text-right py-2 px-3 font-mono">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Drop reasons

const DropReasonsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lead-lifecycle/drop-reasons?token=${token}`);
      setData(res.data?.data);
    } catch (e) {
      toast.error('Could not load drop reasons');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Drop reasons</CardTitle>
          <CardDescription>Why leads are lost — for sales coaching.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <Skeleton className="h-40 w-full" />
        ) : !data ? (
          <div className="text-slate-500">No data.</div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              {data.total_lost} lead{data.total_lost === 1 ? '' : 's'} marked lost in window.
            </p>
            {data.breakdown.map((r) => {
              const max = data.breakdown[0]?.count || 1;
              const pct = max ? (100 * r.count) / max : 0;
              return (
                <div key={r.reason} className="flex items-center gap-3">
                  <div className="w-44 text-sm">{r.label}</div>
                  <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden relative">
                    <div className="h-full bg-red-400/60" style={{ width: `${pct}%` }} />
                    <div className="absolute inset-0 px-2 flex items-center text-xs">
                      {r.count} · {r.pct}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Settings (aging thresholds)

const SettingsTab = ({ token }) => {
  const enums = useLifecycleEnums();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lead-lifecycle/config?token=${token}`);
      setCfg(res.data?.data);
    } catch (e) {
      toast.error('Could not load config');
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/lead-lifecycle/config?token=${token}`, cfg);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (!cfg) return <Skeleton className="h-40 w-full" />;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Aging thresholds</CardTitle>
        <CardDescription>
          Days a lead can sit in a stage before the aging alert fires.
          Set to 0 to disable a stage's check (Closed is auto-disabled).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {enums.stages.filter((s) => s.key !== 'closed').map((s) => (
            <div key={s.key}>
              <label className="text-xs text-slate-600">{s.label}</label>
              <Input
                type="number"
                min="0"
                max="365"
                value={cfg.aging_thresholds?.[s.key] ?? 0}
                onChange={(e) => setCfg({
                  ...cfg,
                  aging_thresholds: {
                    ...(cfg.aging_thresholds || {}),
                    [s.key]: parseInt(e.target.value, 10) || 0,
                  },
                })}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.aging_alerts_enabled !== false}
            onChange={(e) => setCfg({ ...cfg, aging_alerts_enabled: e.target.checked })}
          />
          <span className="text-sm">Aging alerts enabled</span>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
